import React, { memo } from 'react';
import { Dropdown, Switch } from 'tea-component';
import { useIntl } from 'umi';
import SvgIcon from '@home/components/SvgIcon';
import CommonBubble from '@home/components/CommonBubble';
import HistoryChatList from './HistoryChatList';
import './CopilotHeader.scss';
import moreMenuIcon from '@/pages/aigc/assets/svg/more-menu.svg';
import iconCopilotMenuArrow from '@/pages/aigc/assets/svg/icon-copilot-menu-arrow.svg';
import iconCopilotMenuSettings from '@/pages/aigc/assets/svg/icon-copilot-menu-settings.svg';
import type { TCopilotWindowMode } from '../../config/constant';
import type { ChatList } from '@/pages/aigc/layouts/agentLayout/hooks';

/** Copilot 头部「更多」菜单 Props */
interface ICopilotMoreMenuProps {
  chatList: ChatList[];
  bookMarkedChats: ChatList[];
  chatListLoading: boolean;
  newSessionID?: string;
  sessionId?: string;
  /** 选中历史会话后回调（由父级切换 sessionId） */
  onSelectSession: (sessionId: string) => void;
  /** 系统默认窗口模式 */
  defaultWindowMode?: TCopilotWindowMode;
  /** 切换默认窗口模式（同步当前窗口） */
  onChangeDefaultWindowMode?: (mode: TCopilotWindowMode) => void;
}

/**
 * Copilot 标题栏三点菜单：历史对话子菜单 + 默认窗口（悬浮/内嵌）开关。
 */
export const CopilotMoreMenu = memo(
  ({
    chatList,
    bookMarkedChats,
    chatListLoading,
    newSessionID,
    sessionId,
    onSelectSession,
    defaultWindowMode = 'floating',
    onChangeDefaultWindowMode,
  }: ICopilotMoreMenuProps) => {
    const { formatMessage } = useIntl();
    const isDefaultDocked = defaultWindowMode === 'docked';

    return (
      <div
        className="dbh-agent-more-menu-anchor"
        onMouseDown={event => event.stopPropagation()}
      >
        <Dropdown
          appearance="pure"
          className="dbh-agent-more-menu-dropdown"
          overlayClassName="dbh-agent-more-menu-overlay"
          placement="bottom-end"
          clickClose={false}
          closeOnScroll={false}
          destroyOnClose
          button={
            <div className="dbh-agent-title-right-icon dbh-agent-more-menu">
              <img src={moreMenuIcon} alt="" width={20} height={20} />
            </div>
          }
        >
          {close => (
            <div className="dbh-agent-more-menu-panel">
              <div className="dbh-agent-more-menu-section">
                <HistoryChatList
                  newSessionID={newSessionID}
                  chatList={chatList}
                  bookMarkedChats={bookMarkedChats}
                  chatListLoading={chatListLoading}
                  sessionId={sessionId}
                  placement="left-start"
                  placementOffset={[-8, 0]}
                  className="dbh-agent-more-menu-history-dropdown"
                  overlayClassName="dbh-agent-more-menu-history-overlay"
                  onSelectSession={(selectedSessionId: string) => {
                    onSelectSession(selectedSessionId);
                    close();
                  }}
                  button={
                    <div className="dbh-agent-more-menu-item dbh-agent-more-menu-item--history">
                      <span className="dbh-agent-more-menu-item__left">
                        <SvgIcon
                          type="copilot-chat-history"
                          className="dbh-agent-more-menu-item__history-icon"
                        />
                        <span className="dbh-agent-more-menu-item__label">
                          {formatMessage({ id: 'dba_copilot_chat_history' })}
                        </span>
                      </span>
                      <img
                        className="dbh-agent-more-menu-item__arrow-img"
                        src={iconCopilotMenuArrow}
                        alt=""
                        width={16}
                        height={16}
                      />
                    </div>
                  }
                />
              </div>
              <div className="dbh-agent-more-menu-divider" />
              <div className="dbh-agent-more-menu-section">
                <div
                  className="dbh-agent-more-menu-item dbh-agent-more-menu-item--settings"
                  onMouseDown={event => event.stopPropagation()}
                  onClick={event => event.stopPropagation()}
                >
                  <span className="dbh-agent-more-menu-item__left">
                    <img
                      className="dbh-agent-more-menu-item__settings-img"
                      src={iconCopilotMenuSettings}
                      alt=""
                      width={16}
                      height={16}
                    />
                    <span className="dbh-agent-more-menu-item__label">
                      {formatMessage({ id: 'dba_copilot_window_mode' })}
                    </span>
                  </span>
                  <span className="dbh-agent-more-menu-item__right">
                    <span className="dbh-agent-more-menu-item__docked-label">
                      {formatMessage({ id: 'dba_copilot_docked_label' })}
                    </span>
                    <CommonBubble
                      placement="bottom"
                      content={formatMessage({
                        id: isDefaultDocked
                          ? 'dba_copilot_docked_hover_desc'
                          : 'dba_copilot_floating_hover_desc',
                      })}
                      overlayClassName="dba-nav-menu-overlay dbh-agent-more-menu-switch-bubble"
                    >
                      <span className="dbh-agent-more-menu-item__switch-wrap">
                        <Switch
                          value={isDefaultDocked}
                          onChange={(docked: boolean) =>
                            onChangeDefaultWindowMode?.(docked ? 'docked' : 'floating')
                          }
                        />
                      </span>
                    </CommonBubble>
                  </span>
                </div>
              </div>
            </div>
          )}
        </Dropdown>
      </div>
    );
  },
);

CopilotMoreMenu.displayName = 'CopilotMoreMenu';
